import { useTranslation } from "react-i18next";

import { useProfile } from "@/entities/profile";
import { ROUTES } from "@/shared/config";
import { formatMoney } from "@/shared/lib";
import { MenuRow } from "@/shared/ui";
import { ModeSwitch } from "@/features/switch-mode";
import { ProfileCard } from "@/widgets/profile-card";
import { ProfileMetrics } from "@/widgets/profile-metrics";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Профиль.
 *
 * Меню — единственный вход в разделы, которых нет в нижней навигации: там пять
 * фиксированных пунктов, и ни кошелёк, ни рейтинг в них не помещаются. В пакете
 * дизайна на экране рейтинга подсвечен именно таб «Профиль» — то есть оба
 * задуманы как подэкраны отсюда.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useProfile();

  // Заголовок раздела рисуется до данных — как в каталоге и ленте. Иначе на
  // месте экрана до загрузки нет ничего, по чему видно, куда попал.
  if (isPending || isError) {
    return (
      <div className="flex flex-col gap-3 px-[var(--layout-gutter-card)] py-3">
        <TopBar title={t("screens.profile")} subtitle={t("profile.noUsername")} />
        {isError ? (
          <ScreenState
            tone="loss"
            icon="warningFill"
            title={t("states.error.title")}
            body={t("states.error.body")}
            action={{ label: t("states.error.action"), onClick: () => void refetch() }}
          />
        ) : (
          <ScreenSkeleton blocks={[120, 96, 240]} />
        )}
      </div>
    );
  }

  const { user, stats, spark } = data;

  return (
    <div className="flex flex-col gap-3 px-[var(--layout-gutter-card)] py-3">
      <TopBar
        title={t("screens.profile")}
        subtitle={user.username ? `@${user.username}` : t("profile.noUsername")}
      />

      <ProfileCard
        name={user.display_name}
        initial={user.initial}
        avatarUrl={user.avatar_url}
        joined={`${t("profile.since")} ${String(new Date(user.created_at).getFullYear())}`}
        to={ROUTES.profileEdit}
        label={t("profile.edit.open")}
      />

      <ProfileMetrics
        robotsLabel={t("profile.stats.robots")}
        robotsValue={t("profile.stats.robotsValue", {
          active: stats.robots_active,
          total: stats.robots_total,
        })}
        earnedLabel={t("profile.stats.earned")}
        earnedValue={formatMoney(stats.earned_total, { sign: "always", suffix: "USDT" })}
        spark={spark}
      />

      {/* Переключатель режима (доработки §3). В профиле, а не в шапке: смена
          продукта, в котором человек находится, — не то действие, которое
          должно попадаться под палец на каждом экране. Постоянную маркировку
          демо при этом даёт плашка каркаса, а не этот блок. */}
      <section className="flex flex-col gap-2 rounded-[20px] border border-border-subtle bg-background-elevated p-4">
        <span className="text-label font-medium text-text-secondary">
          {t("demo.mode.label")}
        </span>
        <ModeSwitch />
        <span className="text-caption font-normal text-text-tertiary">
          {t("demo.mode.hint")}
        </span>
      </section>

      <nav aria-label={t("profile.menu.label")} className="stagger-children flex flex-col gap-2">
        <MenuRow to={ROUTES.wallet} icon="coinStack" title={t("profile.menu.wallet")} tone="green" />
        <MenuRow to={ROUTES.rating} icon="crown" title={t("profile.menu.rating")} tone="gold" />
        <MenuRow
          to={ROUTES.profileNotifications}
          icon="bell"
          title={t("profile.menu.notifications")}
        />
        <MenuRow
          to={ROUTES.profilePrivacy}
          icon="shieldCheck"
          title={t("profile.menu.privacy")}
          subtitle={t(
            user.public_rating_opt_in ? "profile.privacy.on" : "profile.privacy.off",
          )}
        />
        <MenuRow
          to={ROUTES.profileLanguage}
          icon="translate"
          title={t("profile.menu.language")}
          subtitle={t(`profile.language.${user.language === "en" ? "en" : "ru"}`)}
        />
        <MenuRow
          to={ROUTES.referral}
          icon="shareNetwork"
          title={t("profile.menu.referral")}
          tone="violet"
          badge={
            <span className="shrink-0 rounded-sm border border-status-profit/45 bg-status-profit/10 px-2 py-0.5 text-caption font-medium text-status-profit">
              {t("profile.menu.referralBadge")}
            </span>
          }
        />
        <MenuRow to={ROUTES.support} icon="lifebuoy" title={t("profile.menu.support")} />
      </nav>
    </div>
  );
}
